import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import Header from "@/components/layout/header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import {
  useServiceCategories,
  useServiceTypes,
  useCreateServiceCategory,
  useUpdateServiceCategory,
  useDeleteServiceCategory,
  useCreateServiceSubCategory,
  useUpdateServiceSubCategory,
  useDeleteServiceSubCategory,
  useCreateServiceType,
  useUpdateServiceType,
  useDeleteServiceType
} from "@/hooks/useServiceCategories";
import type {
  ServiceCategory,
  ServiceSubCategory,
  ServiceType,
  ServiceBehaviorType
} from "@/types/serviceCategories";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  MoreHorizontal,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Folder,
  FolderOpen,
  Tag
} from "lucide-react";

// Form schemas
const categoryFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  description: z.string().optional(),
  isActive: z.boolean().default(true)
});

const subCategoryFormSchema = z.object({
  serviceCategoryId: z.string().min(1, "Please select a category"),
  name: z.string().min(2, "Name must be at least 2 characters"),
  description: z.string().optional(),
  isActive: z.boolean().default(true)
});

const serviceTypeFormSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  description: z.string().optional(),
  behaviorType: z.enum(["Bookable", "Claimable"]),
  isActive: z.boolean().default(true)
});

type CategoryFormData = z.infer<typeof categoryFormSchema>;
type SubCategoryFormData = z.infer<typeof subCategoryFormSchema>;
type ServiceTypeFormData = z.infer<typeof serviceTypeFormSchema>;

// Modal components
interface CategoryModalProps {
  open: boolean;
  onClose: () => void;
  category?: ServiceCategory;
}

const CategoryModal = ({ open, onClose, category }: CategoryModalProps) => {
  const createCategory = useCreateServiceCategory();
  const updateCategory = useUpdateServiceCategory();
  
  const form = useForm<CategoryFormData>({
    resolver: zodResolver(categoryFormSchema),
    defaultValues: {
      name: "",
      description: "",
      isActive: true
    }
  });

  // Reset form when category changes
  useEffect(() => {
    if (open) {
      form.reset({
        name: category?.name || "",
        description: category?.description || "",
        isActive: category?.isActive ?? true
      });
    }
  }, [category, open, form]);

  const onSubmit = async (data: CategoryFormData) => {
    try {
      if (category) {
        await updateCategory.mutateAsync({ id: category.id, data });
      } else {
        await createCategory.mutateAsync(data);
      }
      form.reset();
      onClose();
    } catch (error) {
      // Error handled by hooks
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {category ? "Edit Category" : "Create Category"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter category name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Enter category description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <FormLabel>Active</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={createCategory.isPending || updateCategory.isPending}
              >
                {category ? "Update" : "Create"} Category
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

// Sub-categories are flattened from their parent category, so each carries the
// parent's id (serviceCategoryId) even though the base ServiceSubCategory shape omits it.
type SubCategoryWithParent = ServiceSubCategory & { serviceCategoryId: string };

interface SubCategoryModalProps {
  open: boolean;
  onClose: () => void;
  subCategory?: SubCategoryWithParent;
  categories: ServiceCategory[];
}

const SubCategoryModal = ({ open, onClose, subCategory, categories }: SubCategoryModalProps) => {
  const createSubCategory = useCreateServiceSubCategory();
  const updateSubCategory = useUpdateServiceSubCategory();
  
  const form = useForm<SubCategoryFormData>({
    resolver: zodResolver(subCategoryFormSchema),
    defaultValues: {
      serviceCategoryId: "",
      name: "",
      description: "",
      isActive: true
    }
  });

  // Reset form when subCategory changes
  useEffect(() => {
    if (open) {
      form.reset({
        serviceCategoryId: subCategory?.serviceCategoryId || "",
        name: subCategory?.name || "",
        description: subCategory?.description || "",
        isActive: subCategory?.isActive ?? true
      });
    }
  }, [subCategory, open, form]);

  const onSubmit = async (data: SubCategoryFormData) => {
    try {
      const { serviceCategoryId, ...payload } = data;
      if (subCategory) {
        await updateSubCategory.mutateAsync({
          categoryId: serviceCategoryId,
          subcategoryId: subCategory.id,
          data: payload,
        });
      } else {
        await createSubCategory.mutateAsync({ categoryId: serviceCategoryId, data: payload });
      }
      form.reset();
      onClose();
    } catch (error) {
      // Error handled by hooks
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {subCategory ? "Edit Sub-Category" : "Create Sub-Category"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="serviceCategoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Parent Category</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {categories.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter sub-category name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Enter sub-category description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <FormLabel>Active</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={createSubCategory.isPending || updateSubCategory.isPending}
              >
                {subCategory ? "Update" : "Create"} Sub-Category
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

interface ServiceTypeModalProps {
  open: boolean;
  onClose: () => void;
  serviceType?: ServiceType;
}

const ServiceTypeModal = ({ open, onClose, serviceType }: ServiceTypeModalProps) => {
  const createServiceType = useCreateServiceType();
  const updateServiceType = useUpdateServiceType();
  
  const form = useForm<ServiceTypeFormData>({
    resolver: zodResolver(serviceTypeFormSchema),
    defaultValues: {
      name: "",
      description: "",
      behaviorType: "Bookable",
      isActive: true
    }
  });

  // Reset form when serviceType changes
  useEffect(() => {
    if (open) {
      form.reset({
        name: serviceType?.name || "",
        description: serviceType?.description || "",
        behaviorType: serviceType?.behaviorType || "Bookable",
        isActive: serviceType?.isActive ?? true
      });
    }
  }, [serviceType, open, form]);

  const onSubmit = async (data: ServiceTypeFormData) => {
    try {
      if (serviceType) {
        await updateServiceType.mutateAsync({ id: serviceType.id, data });
      } else {
        await createServiceType.mutateAsync(data);
      }
      form.reset();
      onClose();
    } catch (error) {
      // Error handled by hooks
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {serviceType ? "Edit Service Type" : "Create Service Type"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter service type name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Enter service type description" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="behaviorType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Behavior Type</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select behavior type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Bookable">Bookable</SelectItem>
                      <SelectItem value="Claimable">Claimable</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex items-center space-x-2">
                  <FormLabel>Active</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={createServiceType.isPending || updateServiceType.isPending}
              >
                {serviceType ? "Update" : "Create"} Service Type
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

// Main component
export default function ServiceCategories() {
  const [activeTab, setActiveTab] = useState("categories");
  const [searchQuery, setSearchQuery] = useState("");
  const [includeInactive, setIncludeInactive] = useState(false);
  
  // Modal states
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [subCategoryModalOpen, setSubCategoryModalOpen] = useState(false);
  const [serviceTypeModalOpen, setServiceTypeModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | undefined>();
  const [editingSubCategory, setEditingSubCategory] = useState<SubCategoryWithParent | undefined>();
  const [editingServiceType, setEditingServiceType] = useState<ServiceType | undefined>();
  
  // API hooks - Single source of truth: categories with nested subcategories
  const { data: categories = [], isLoading: loadingCategories, error: categoriesError } = useServiceCategories(true, includeInactive);
  const { data: serviceTypes = [], isLoading: loadingServiceTypes } = useServiceTypes();

  // Extract flattened subcategories from categories for display in subcategories tab
  const allSubCategories = React.useMemo(() => {
    const flattened: SubCategoryWithParent[] = [];
    
    categories.forEach(category => {
      if (category.serviceSubCategories) {
        category.serviceSubCategories.forEach(subCat => {
          flattened.push({
            ...subCat,
            serviceCategoryId: category.id
          });
        });
      }
    });
    
    return flattened;
  }, [categories]);

  // For lookup purposes, use the same categories data
  const allCategoriesForLookup = categories;
  
  const deleteCategory = useDeleteServiceCategory();
  const deleteSubCategory = useDeleteServiceSubCategory();
  const deleteServiceType = useDeleteServiceType();

  // Filter functions
  const filteredCategories = categories.filter(category =>
    category.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSubCategories = allSubCategories.filter(subCategory =>
    subCategory.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    subCategory.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredServiceTypes = serviceTypes.filter(serviceType =>
    serviceType.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    serviceType.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Event handlers
  const handleEditCategory = (category: ServiceCategory) => {
    setEditingCategory(category);
    setCategoryModalOpen(true);
  };

  const handleEditSubCategory = (subCategory: SubCategoryWithParent) => {
    setEditingSubCategory(subCategory);
    setSubCategoryModalOpen(true);
  };

  const handleEditServiceType = (serviceType: ServiceType) => {
    setEditingServiceType(serviceType);
    setServiceTypeModalOpen(true);
  };

  const handleCloseModals = () => {
    setCategoryModalOpen(false);
    setSubCategoryModalOpen(false);
    setServiceTypeModalOpen(false);
    setEditingCategory(undefined);
    setEditingSubCategory(undefined);
    setEditingServiceType(undefined);
  };

  const getCategoryName = (categoryId: string) => {
    return allCategoriesForLookup.find(cat => cat.id === categoryId)?.name || "Unknown Category";
  };

  if (categoriesError) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Service Categories" description="Manage service categories, sub-categories, and types">
          <Button disabled>
            <Plus className="w-4 h-4 mr-2" />
            Add Category
          </Button>
        </Header>
        <div className="flex-1 p-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error loading categories</AlertTitle>
            <AlertDescription>
              Failed to load service categories. Please try refreshing the page.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <Header 
        title="Service Categories" 
        description="Manage service categories, sub-categories, and types"
      >
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIncludeInactive(!includeInactive)}
          >
            {includeInactive ? "Hide Inactive" : "Show Inactive"}
          </Button>
          {activeTab === "categories" && (
            <Button onClick={() => setCategoryModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          )}
          {activeTab === "subcategories" && (
            <Button onClick={() => setSubCategoryModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Sub-Category
            </Button>
          )}
          {activeTab === "types" && (
            <Button onClick={() => setServiceTypeModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Service Type
            </Button>
          )}
        </div>
      </Header>
      
      <div className="flex-1 overflow-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="categories" className="flex items-center gap-2">
              <Folder className="w-4 h-4" />
              Categories ({filteredCategories.length})
            </TabsTrigger>
            <TabsTrigger value="subcategories" className="flex items-center gap-2">
              <FolderOpen className="w-4 h-4" />
              Sub-Categories ({filteredSubCategories.length})
            </TabsTrigger>
            <TabsTrigger value="types" className="flex items-center gap-2">
              <Tag className="w-4 h-4" />
              Service Types ({filteredServiceTypes.length})
            </TabsTrigger>
          </TabsList>

          {/* Search and Filters */}
          <Card className="mt-6">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Categories Tab */}
          <TabsContent value="categories" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Service Categories</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingCategories ? (
                  <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Sub-Categories</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredCategories.map((category) => (
                        <TableRow key={category.id}>
                          <TableCell className="font-medium">{category.name}</TableCell>
                          <TableCell>{category.description || "-"}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {category.serviceSubCategories?.length || 0} sub-categories
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={category.isActive ? "default" : "secondary"}>
                              {category.isActive ? (
                                <><CheckCircle2 className="w-3 h-3 mr-1" />Active</>
                              ) : (
                                <><XCircle className="w-3 h-3 mr-1" />Inactive</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditCategory(category)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Category</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete "{category.name}"? This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => deleteCategory.mutate(category.id)}
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sub-Categories Tab */}
          <TabsContent value="subcategories" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Service Sub-Categories</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingCategories ? (
                  <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Parent Category</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSubCategories.map((subCategory) => (
                        <TableRow key={subCategory.id}>
                          <TableCell className="font-medium">{subCategory.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {getCategoryName(subCategory.serviceCategoryId)}
                            </Badge>
                          </TableCell>
                          <TableCell>{subCategory.description || "-"}</TableCell>
                          <TableCell>
                            <Badge variant={subCategory.isActive ? "default" : "secondary"}>
                              {subCategory.isActive ? (
                                <><CheckCircle2 className="w-3 h-3 mr-1" />Active</>
                              ) : (
                                <><XCircle className="w-3 h-3 mr-1" />Inactive</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditSubCategory(subCategory)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Sub-Category</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete "{subCategory.name}"? This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => deleteSubCategory.mutate({ categoryId: subCategory.serviceCategoryId, subcategoryId: subCategory.id })}
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Service Types Tab */}
          <TabsContent value="types" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Service Types</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingServiceTypes ? (
                  <div className="space-y-2">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Service Behavior Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredServiceTypes.map((serviceType) => (
                        <TableRow key={serviceType.id}>
                          <TableCell className="font-medium">{serviceType.name}</TableCell>
                          <TableCell>{serviceType.description || "-"}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {serviceType.behaviorType || "Unknown"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={serviceType.isActive ? "default" : "secondary"}>
                              {serviceType.isActive ? (
                                <><CheckCircle2 className="w-3 h-3 mr-1" />Active</>
                              ) : (
                                <><XCircle className="w-3 h-3 mr-1" />Inactive</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditServiceType(serviceType)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm">
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Service Type</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete "{serviceType.name}"? This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => deleteServiceType.mutate(serviceType.id)}
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modals */}
      <CategoryModal
        open={categoryModalOpen}
        onClose={handleCloseModals}
        category={editingCategory}
      />
      
      <SubCategoryModal
        open={subCategoryModalOpen}
        onClose={handleCloseModals}
        subCategory={editingSubCategory}
        categories={allCategoriesForLookup}
      />
      
      <ServiceTypeModal
        open={serviceTypeModalOpen}
        onClose={handleCloseModals}
        serviceType={editingServiceType}
      />
    </div>
  );
}